import { ChevronRightIcon, LockIcon, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"
import { Link, type To } from "react-router"
import { cn } from "@/primitives/utils"

/** How far a tree row is set in past its own padding, a level at a time, as the sidebar's are. */
const indentPerLevel = 4

/**
 * One row of a tree of links, as the Documents page's Folders are: a rounded row as the sidebar's,
 * 32px on the desk and a stacked row's height on `compact`, set in 16px for each level it stands at
 * (`depth`, however deep). A row holding rows beneath it leads with a chevron that shows or hides
 * them (`disclosure`), outside its link so either can be reached alone; a leaf keeps the chevron's
 * room, so every icon lines up. The row's link is a `data-row` that `useRowNavigation` moves
 * through; `current` fills the row as the one chosen. `readOnly` ends it with a lock, which a
 * screen reader hears as "Read only"; `trailing` ends it otherwise.
 */
export function TreeRow({
  to,
  replace,
  current = false,
  depth = 0,
  icon: Icon,
  name,
  readOnly = false,
  disclosure,
}: {
  to: To
  /** Whether choosing it replaces the history entry rather than adding one. */
  replace?: boolean
  current?: boolean
  /** How deep in the tree it stands: 0 at the top. */
  depth?: number
  icon: LucideIcon
  name: ReactNode
  readOnly?: boolean
  /** Whether the rows beneath it show, and what shows or hides them; none for a leaf. */
  disclosure?: { open: boolean; onToggle: () => void; label: string }
}) {
  return (
    <div
      className={cn(
        "group/row relative mx-2 flex h-8 items-center gap-1 rounded-md pr-2 transition-colors duration-100 ease-snappy compact:mx-0 compact:h-12 compact:rounded-none compact:border-b compact:border-border-subtle compact:pr-4",
        current
          ? "bg-selected"
          : "has-[[data-highlighted]]:bg-accent has-[a:focus-visible]:bg-accent has-[a:hover]:bg-accent",
      )}
      // Set in by its depth past the chevron's own room, a level being four spacing units.
      style={{ paddingLeft: `calc(var(--spacing) * ${1 + indentPerLevel * Math.max(depth, 0)})` }}
    >
      {disclosure ? (
        <button
          type="button"
          aria-expanded={disclosure.open}
          aria-label={disclosure.label}
          onClick={disclosure.onToggle}
          className="flex size-6 shrink-0 items-center justify-center rounded-sm text-subtle-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRightIcon
            aria-hidden
            className={cn(
              "size-3.5 transition-transform duration-100 ease-snappy",
              disclosure.open && "rotate-90",
            )}
          />
        </button>
      ) : (
        <span aria-hidden className="size-6 shrink-0" />
      )}
      <Link
        to={to}
        replace={replace ?? false}
        data-row
        aria-current={current ? "page" : undefined}
        className="flex h-full min-w-0 flex-1 items-center gap-2 rounded-md outline-none"
      >
        <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-ui font-medium">{name}</span>
        {readOnly && (
          <>
            <LockIcon aria-hidden className="size-3.5 shrink-0 text-subtle-foreground" />
            <span className="sr-only">Read only</span>
          </>
        )}
      </Link>
    </div>
  )
}
